import { Injectable, OnDestroy } from '@angular/core';
import { HttpInterceptor, HttpHandler, HttpRequest, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, NEVER, Subscription } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { ErrorStateService } from '../core/error.service';
import { LoggingService } from '../core/logging.service';
import { Router } from '@angular/router';
import { AuthService } from '../../../user/services/auth.service';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor, OnDestroy {
  constructor(
    private errorStateService: ErrorStateService,
    private logService: LoggingService,
    private translate: TranslateService,
    private router: Router,
    private authService: AuthService
  ) {}

  translationSub: Subscription;

  ngOnDestroy() {
    if (this.translationSub) {
      this.translationSub.unsubscribe();
    }
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      tap(() => {
        this.errorStateService.clearErrors();
      }),
      catchError((response: HttpErrorResponse) => {
        if (response.error instanceof ErrorEvent) {
          // This is a client side error so we don't need to add this to the errors list.
          this.logService.error('[Http Error Interceptor] Client side error', response);
          return NEVER;
        } else if (response.status === 400) {
          const errorMessage = this.getTranslation(response);
          this.errorStateService.addError(errorMessage);
          this.logService.info('[Http Error Interceptor] 400 Bad Request', response.error);
        } else if (response.status === 401) {
          // handled by the token-refresh interceptor
        } else if (response.status === 403) {
          // handled by the first-time-registration interceptor
        } else if (response.status === 500) {
          this.errorStateService.addError('error.unexpectedError');
          this.logService.error('[Http Error Interceptor] 500 Internal Server Error', response.error);
        } else if (response.status === 503) {
          this.errorStateService.addError('error.unexpectedError');
          this.logService.error('[Http Error Interceptor] 503 Service Unavailable');
          if(this.authService.hasCurrentRefreshToken()) {
            this.authService.logout().toPromise();
          }
          this.router.navigate(['/maintenance']);
        } else {
          // The error codes returned should be added to the error service to be displayed to the user.
          let error = `error.${response.error}`;
          this.translationSub = this.translate.get(error).subscribe((data: string) => {
            /** if translation is found, return translation. If none found, return original error */
            if (data == error) {
              this.errorStateService.addError(response.error);
            } else {
              this.errorStateService.addError(data);
            }
          });
          this.logService.error(`[Http Error Interceptor] ${response.status} Uncaught server side error`, response.error);
        }
        return throwError(() => response);
      })
    );
  }

  getTranslation(error: HttpErrorResponse): string{
    const errorCode = error.error.Code ? error.error.Code : error.error;
    const translatedText = this.translate.instant(`error.${errorCode}`);
    return translatedText ? translatedText : error.error.Message ? error.error.Message : this.translate.instant(`error.${error.status}.unexpected.occured`);
  }
}
